import React, { useState, useMemo, useRef, useEffect } from "react";
import { DateTime } from "luxon";
import {
    DateRangePickers,
    DATES,
    AppBar,
    Box,
    Collapse,
    IconButton,
    Tab,
    Tabs,
    Typography,
    ExpandMore,
    ExpandLess,
    MuiIcons
} from "@leap4/frontend";
import { tabsClasses } from "@mui/material";

const {
    CalendarMonthTwoTone: MonthIcon,
    CalendarViewDayTwoTone: MTDIcon,
    DateRangeTwoTone: WeekIcon,
    EventNoteTwoTone: Last30Icon,
    EventRepeatTwoTone: CustomDateIcon,
    InsertInvitationTwoTone: TodayIcon,
    TodayTwoTone: YesterdayIcon,
} = MuiIcons;

export interface IMobileTimeNavbarProps {
    dates: { startDate: string; endDate: string };
    setDates: (startDate: string, endDate: string) => void;
    isLoading: boolean;
    excludeTabs?: Array<DATES>;
    disableStartDate?: (day: any) => boolean;
    disableEndDate?: (day: any) => boolean;
    lastAvailableDate?: string;
    topOffset?: number;
}

const DEFAULT_TOP_OFFSET = 50;
const COLLAPSED_HEIGHT = 96; // Accurate initial height: header(~36) + tabs(~48) + padding(~12)

export const MobileTimeNavbar = (props: IMobileTimeNavbarProps) => {
    const { dates, setDates, isLoading, excludeTabs, disableStartDate, disableEndDate, topOffset = DEFAULT_TOP_OFFSET } = props;
    const [isDatePickerExpanded, setIsDatePickerExpanded] = useState(false);
    const [navbarHeight, setNavbarHeight] = useState(COLLAPSED_HEIGHT);
    const appBarRef = useRef<HTMLDivElement>(null);
    
    // Measure AppBar height after expansion changes (for expand/collapse animation)
    useEffect(() => {
        // Only measure after Collapse animation settles
        const timer = setTimeout(() => {
            if (appBarRef.current) {
                setNavbarHeight(appBarRef.current.offsetHeight);
            }
        }, 350);
        return () => clearTimeout(timer);
    }, [isDatePickerExpanded]);

    const toggleExpanded = () => {
        setIsDatePickerExpanded(!isDatePickerExpanded);
    };

    const lastAvailableDateUTC = props.lastAvailableDate
        ? DateTime.fromISO(props.lastAvailableDate, { zone: "utc" })
        : DateTime.now().minus({ days: 1 });

    const datePresets = useMemo(
        () =>
            [
                {
                    key: DATES.TODAY,
                    label: "Today",
                    startDate: DateTime.now().toISODate(),
                    endDate: DateTime.now().toISODate(),
                    icon: <TodayIcon fontSize="small" />,
                },
                {
                    key: DATES.YESTERDAY,
                    label: "Yesterday",
                    startDate: lastAvailableDateUTC.toISODate(),
                    endDate: lastAvailableDateUTC.toISODate(),
                    icon: <YesterdayIcon fontSize="small" />,
                },
                {
                    key: DATES.LAST_SEVEN_DAYS,
                    label: "7 Days",
                    startDate: lastAvailableDateUTC.minus({ days: 6 }).toISODate(),
                    endDate: lastAvailableDateUTC.toISODate(),
                    icon: <WeekIcon fontSize="small" />,
                },
                {
                    key: DATES.LAST_THIRTY_DAYS,
                    label: "30 Days",
                    startDate: lastAvailableDateUTC.minus({ days: 29 }).toISODate(),
                    endDate: lastAvailableDateUTC.toISODate(),
                    icon: <Last30Icon fontSize="small" />,
                },
                {
                    key: DATES.LAST_MONTH,
                    label: "Last Month",
                    startDate: DateTime.now().minus({ months: 1 }).startOf("month").toISODate(),
                    endDate: DateTime.now().minus({ months: 1 }).endOf("month").toISODate(),
                    icon: <MonthIcon fontSize="small" />,
                },
                {
                    key: DATES.MTD,
                    label: "MTD",
                    startDate: DateTime.now().startOf("month").toISODate(),
                    endDate: lastAvailableDateUTC.toISODate(),
                    icon: <MTDIcon fontSize="small" />,
                },
                {
                    key: DATES.CUSTOM,
                    label: "Custom",
                    startDate: lastAvailableDateUTC.minus({ days: 2 }).toISODate(),
                    endDate: lastAvailableDateUTC.toISODate(),
                    icon: <CustomDateIcon fontSize="small" />,
                },
            ].filter((o) => excludeTabs?.indexOf(o.key) === -1),
        [excludeTabs, lastAvailableDateUTC]
    );

    const getSelectedPresetKey = (): DATES | null => {
        const selectedStartDate = DateTime.fromISO(dates.startDate).toISODate();
        const selectedEndDate = DateTime.fromISO(dates.endDate).toISODate();

        const matchingPreset = datePresets.find(
            (preset) =>
                preset.startDate === selectedStartDate && preset.endDate === selectedEndDate
        );

        return matchingPreset?.key || null;
    };

    const handlePresetClick = (preset: (typeof datePresets)[0]) => {
        if (preset.key === DATES.CUSTOM) {
            // Expand the date picker when Custom is selected
            setIsDatePickerExpanded(true);
        } else {
            setDates(preset.startDate!, preset.endDate!);
        }
    };

    const selectedPresetKey = getSelectedPresetKey();
    
    // Get the index of the selected preset for the Tabs component
    const getSelectedTabIndex = (): number => {
        const index = datePresets.findIndex(preset => preset.key === selectedPresetKey);
        return index === -1 ? datePresets.findIndex(p => p.key === DATES.CUSTOM) : index;
    };

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        const preset = datePresets[newValue];
        if (preset) {
            handlePresetClick(preset);
        }
    };

    return (
        <>
            {/* Fixed header with date display, tabs, AND expandable date picker */}
            <AppBar
                ref={appBarRef}
                position="static"
                color="default"
                sx={{
                    left: "1px",
                    top: `${DEFAULT_TOP_OFFSET}px`,
                    position: "fixed",
                    zIndex: "modal",
                    width: "100%",
                    margin: "0 auto",
                }}
            >
                <Box sx={{ px: 1, py: 0.5 }}>
                    {/* Collapsible Date Picker Header */}
                    <Box
                        onClick={toggleExpanded}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                            py: 0.5,
                            px: 1,
                            borderRadius: 1,
                            bgcolor: "action.hover",
                        }}
                    >
                        <Typography variant="body2" fontWeight="medium">
                            {dates.startDate === dates.endDate
                                ? DateTime.fromISO(dates.startDate).toFormat("MMM dd, yyyy")
                                : `${DateTime.fromISO(dates.startDate).toFormat("MMM dd")} - ${DateTime.fromISO(dates.endDate).toFormat("MMM dd, yyyy")}`}
                        </Typography>
                        <IconButton size="small" sx={{ p: 0 }}>
                            {isDatePickerExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>

                    {/* Tabs-style Presets with scroll arrows */}
                    <Tabs
                        value={getSelectedTabIndex()}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            minHeight: 40,
                            [`& .${tabsClasses.scrollButtons}`]: {
                                '&.Mui-disabled': { opacity: 0.3 },
                            },
                            '& .MuiTab-root': {
                                minHeight: 40,
                                minWidth: 'auto',
                                px: 1.5,
                                py: 0.5,
                                textTransform: 'capitalize',
                                fontSize: '0.8rem',
                            },
                        }}
                    >
                        {datePresets.map((preset) => (
                            <Tab
                                key={preset.key}
                                icon={preset.icon}
                                iconPosition="start"
                                label={preset.label}
                                disabled={isLoading}
                            />
                        ))}
                    </Tabs>

                    {/* Expandable Date Picker - inside the fixed AppBar */}
                    <Collapse in={isDatePickerExpanded} timeout={300}>
                        <Box sx={{ py: 1 }}>
                            <DateRangePickers
                                startDate={DateTime.fromJSDate(new Date(dates.startDate))
                                    .plus({ minutes: new Date(dates.startDate).getTimezoneOffset() })
                                    .toJSDate()}
                                endDate={DateTime.fromJSDate(new Date(dates.endDate))
                                    .plus({ minutes: new Date(dates.endDate).getTimezoneOffset() })
                                    .toJSDate()}
                                buttonProps={{ disabled: isLoading, size: "small" }}
                                textFieldProps={{ size: "small", fullWidth: true }}
                                disableFuture={true}
                                shouldDisableDateStart={disableStartDate}
                                shouldDisableDateEnd={disableEndDate}
                                onSearch={(ev, dts) => {
                                    setDates(
                                        DateTime.fromJSDate(dts.startDate).toISODate()!,
                                        DateTime.fromJSDate(dts.endDate).toISODate()!
                                    );
                                }}
                                style={{ justifyContent: "center" }}
                            />
                        </Box>
                    </Collapse>
                </Box>
            </AppBar>

            {/* Dynamic spacer - matches actual AppBar height */}
            <Box sx={{ height: `${navbarHeight}px`, transition: 'height 0.3s ease' }} />
        </>
    );
};

export default MobileTimeNavbar;
